/**
 * Módulo de Movilidad — Pasajes y Combustible
 * Diseño aplicando: Claridad, Jerarquía visual, Espaciado 8pt, Consistencia,
 * Simplicidad / Progressive disclosure, UX Writing, Feedback visual,
 * Motion design funcional, Accesibilidad, Familiaridad, Gestalt, Tipografía,
 * Teoría del color, Minimalismo funcional, Psicología cognitiva.
 */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { http } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import {
  Plus, Search, X, Save, Car, Edit, Trash2, Eye,
  TrendingUp, TrendingDown, Wallet, Camera, BarChart3,
  CalendarDays, History, Users, ChevronDown, AlertCircle,
  CheckCircle2, Loader2, Filter, RefreshCw, DollarSign,
} from 'lucide-react';
import { toast } from 'sonner';

/* ─── DESIGN TOKENS ────────────────────────────────────────────────────────── */
const RING = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2';
const SEM = {
  success: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200',
  danger:  'bg-red-50   text-red-700   ring-1 ring-inset ring-red-200',
  amber:   'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200',
};

const ASUNCION_TZ = 'America/Asuncion';

const TIPOS = [
  { value: 'pasaje',      label: 'Pasaje',      icon: '🚌', ring: 'ring-sky-300',   bg: 'bg-sky-50',   text: 'text-sky-800'   },
  { value: 'combustible', label: 'Combustible', icon: '⛽', ring: 'ring-amber-300', bg: 'bg-amber-50', text: 'text-amber-800' },
];

const MESES = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre',
];

/* ─── PURE UTILS ────────────────────────────────────────────────────────────── */
const titleCase = (s = '') =>
  String(s).trim().split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');

const displayName = (p) =>
  titleCase(p?.nombre_completo || p?.nombre || p?.name || p?.username || '');

const formatGs = (n) => {
  if (n == null || n === '') return '—';
  return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG', maximumFractionDigits: 0 }).format(n);
};

function parseFecha(value) {
  if (!value) return 0;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value))
    return new Date(`${value}T12:00:00`).getTime() || 0;
  return new Date(value).getTime() || 0;
}

function formatFecha(value) {
  if (!value) return '—';
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00.*)?$/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  }
  const ts = parseFecha(value);
  if (!ts) return '—';
  return new Intl.DateTimeFormat('es-PY', { timeZone: ASUNCION_TZ, day:'2-digit', month:'2-digit', year:'numeric' }).format(new Date(ts));
}

function tecnicoNombre(mov, lista = []) {
  const t = lista.find(t => (t.id || t._id) === mov.tecnico_id);
  return displayName(t) || titleCase(mov.nombre_tecnico || '') || mov.tecnico_id || '—';
}

/* ─── ATOMS ─────────────────────────────────────────────────────────────────── */

function Pill({ tipo, deleted }) {
  if (deleted) return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${SEM.danger}`}>
      <X className="w-3 h-3"/>Eliminado
    </span>
  );
  const t = TIPOS.find(x => x.value === tipo) || TIPOS[0];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${t.bg} ${t.text} ring-1 ring-inset ${t.ring}`}>
      <span>{t.icon}</span>{t.label}
    </span>
  );
}

function Btn({ children, onClick, disabled, loading: isLoading, variant = 'primary', className = '', type = 'button', ...rest }) {
  const styles = {
    primary:   `bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white ${RING} rounded-xl px-4 py-2.5 text-sm font-semibold shadow-sm transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed`,
    secondary: `bg-white hover:bg-slate-50 active:bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-300 ${RING} rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-150`,
    ghost:     `hover:bg-slate-100 active:bg-slate-200 text-slate-600 ${RING} rounded-lg p-2 transition-all duration-150`,
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled || isLoading}
      className={`inline-flex items-center justify-center gap-2 ${styles[variant]} ${className}`} {...rest}>
      {isLoading && <Loader2 className="w-4 h-4 animate-spin"/>}
      {children}
    </button>
  );
}

function MetricCard({ icon: Icon, label, value, colorClass }) {
  return (
    <div className="rounded-xl sm:rounded-2xl border border-slate-200 bg-white p-2.5 sm:p-4 shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-px">
      <div className="flex flex-col items-center gap-1 sm:flex-row sm:items-start sm:gap-3">
        <div className={`flex h-8 w-8 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-lg sm:rounded-xl ${colorClass}`}>
          <Icon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true"/>
        </div>
        <div className="min-w-0 flex-1 text-center sm:text-left">
          <p className="text-[9px] sm:text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500 mb-0.5 leading-tight">{label}</p>
          <p className="truncate text-xs sm:text-base font-bold text-slate-900 tabular-nums">{value}</p>
        </div>
      </div>
    </div>
  );
}

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      {[1,2,3,4,5,6].map(i => (
        <td key={i} className="px-4 py-3">
          <div className="h-4 rounded-md bg-slate-100" style={{ width: `${40 + i*8}%` }}/>
        </td>
      ))}
    </tr>
  );
}

function SkeletonCard() {
  return (
    <div className="animate-pulse rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex gap-3">
        <div className="h-10 w-10 rounded-xl bg-slate-100"/>
        <div className="flex-1 space-y-2 pt-1">
          <div className="h-3 rounded bg-slate-100 w-3/4"/>
          <div className="h-3 rounded bg-slate-100 w-1/2"/>
        </div>
      </div>
    </div>
  );
}

/* ─── FORM FIELDS ────────────────────────────────────────────────────────────── */
function FField({ label, hint, value, onChange, type = 'text', placeholder, readOnly, required }) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-semibold text-slate-700">
        {label}{required && <span className="ml-0.5 text-red-500" aria-hidden="true">*</span>}
      </label>
      <input
        type={type}
        value={value ?? ''}
        onChange={e => onChange && onChange(e.target.value)}
        placeholder={placeholder}
        readOnly={readOnly}
        disabled={readOnly}
        required={required}
        aria-label={label}
        className={`w-full rounded-xl border px-3 py-2.5 text-sm transition-all duration-150 placeholder:text-slate-400
          ${readOnly
            ? 'border-slate-200 bg-slate-50 text-slate-500 cursor-not-allowed'
            : 'border-slate-300 bg-white text-slate-900 hover:border-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500'
          }`}
      />
      {hint && <p className="text-[11px] text-slate-500">{hint}</p>}
    </div>
  );
}

function FSelect({ label, value, onChange, children, required, hint }) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-semibold text-slate-700">
        {label}{required && <span className="ml-0.5 text-red-500" aria-hidden="true">*</span>}
      </label>
      <div className="relative">
        <select
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          required={required}
          aria-label={label}
          className="w-full appearance-none rounded-xl border border-slate-300 bg-white px-3 py-2.5 pr-9 text-sm text-slate-900 transition-all duration-150 hover:border-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {children}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true"/>
      </div>
      {hint && <p className="text-[11px] text-slate-500">{hint}</p>}
    </div>
  );
}

function FTextarea({ label, value, onChange, placeholder, hint }) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-semibold text-slate-700">{label}</label>
      <textarea
        rows={2}
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={label}
        className="w-full resize-none rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 transition-all duration-150 placeholder:text-slate-400 hover:border-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
      {hint && <p className="text-[11px] text-slate-500">{hint}</p>}
    </div>
  );
}

function DetailRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex gap-3 py-2 border-b border-slate-100 last:border-0">
      <span className="w-36 shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</span>
      <span className="flex-1 text-sm text-slate-800">{value}</span>
    </div>
  );
}

/* ─── MODAL WRAPPER (Portal) ────────────────────────────────────────────────── */
function Modal({ onClose, children, size = 'md' }) {
  const sizes = { sm: 'sm:max-w-sm', md: 'sm:max-w-lg', lg: 'sm:max-w-2xl' };
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', h);
    };
  }, [onClose]);
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center"
      role="dialog" aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose} aria-hidden="true"
      />
      <div className={`relative w-full ${sizes[size]} flex flex-col bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[92dvh] sm:max-h-[88dvh]`}>
        <div className="mx-auto mt-3 h-1 w-10 rounded-full bg-slate-300 sm:hidden" aria-hidden="true"/>
        {children}
      </div>
    </div>,
    document.body
  );
}

/* ─── MAIN COMPONENT ─────────────────────────────────────────────────────────── */
export default function MovilidadList() {
  const { user } = useAuth();
  const miId     = user?.id || user?._id || '';
  const esAdmin  = user?.rol === 'admin';
  const esTecnico = user?.rol === 'tecnico';
  const miNombre  = displayName(user);
  const hoy       = new Date();
  const currentYear = hoy.getFullYear();

  const [movimientos, setMovimientos] = useState([]);
  const [tecnicos,    setTecnicos]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);
  const [showForm,    setShowForm]    = useState(false);
  const [editando,    setEditando]    = useState(null);
  const [detalle,     setDetalle]     = useState(null);
  const [acreditando, setAcreditando] = useState(null);
  const [seleccionando, setSeleccionando] = useState(false);
  const [seccion,     setSeccion]     = useState('lista');

  const [q,          setQ]          = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroTec,  setFiltroTec]  = useState('');

  const [histTecnico, setHistTecnico] = useState(esAdmin ? 'all' : miId);
  const [histAnio,    setHistAnio]    = useState(currentYear);
  const [histMes,     setHistMes]     = useState(hoy.getMonth() + 1);
  const [histData,    setHistData]    = useState(null);
  const [histLoading, setHistLoading] = useState(false);
  const [histError,   setHistError]   = useState(null);

  useEffect(() => { cargar(); }, []);
  useEffect(() => { if (esTecnico) setHistTecnico(miId); }, [esTecnico, miId]);
  useEffect(() => { if (seccion === 'historial') cargarHistorial(); }, [histTecnico, histAnio, histMes, seccion]);

  async function cargar(silent = false) {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const res = await http.get('/movilidad');
      const sorted = [...(res.data || [])].sort((a, b) => parseFecha(b.fecha || b.created_at) - parseFecha(a.fecha || a.created_at));
      setMovimientos(sorted);
      if (esAdmin) {
        const uRes = await http.get('/usuarios');
        setTecnicos((uRes.data || []).filter(u => u.rol === 'tecnico'));
      }
    } catch {
      toast.error('No pudimos cargar los movimientos. Intentá de nuevo.');
    } finally { setLoading(false); setRefreshing(false); }
  }

  async function cargarHistorial() {
    setHistLoading(true); setHistError(null);
    try {
      const params = {
        tecnico_id: esAdmin ? (histTecnico || 'all') : miId,
        anio: histAnio || undefined,
        mes:  histMes  || undefined,
      };
      const res = await http.get('/movilidad/historial', { params });
      setHistData(res.data || null);
    } catch (e) {
      setHistError(e.response?.data?.detail || 'No pudimos cargar el historial.');
      setHistData(null);
    } finally { setHistLoading(false); }
  }

  async function eliminar(id) {
    if (!window.confirm('¿Eliminar este movimiento? La acción queda registrada en el historial.')) return;
    try {
      await http.delete(`/movilidad/${id}`);
      toast.success('Movimiento eliminado y registrado en el historial.');
      cargar(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No pudimos eliminar el movimiento.');
    }
  }

  const filtrados = movimientos.filter(m => {
    const txt = q.toLowerCase();
    const matchQ = !txt || [m.descripcion, m.nombre_tecnico, m.tipo_movimiento, m.origen, m.destino]
      .some(v => (v || '').toLowerCase().includes(txt));
    return matchQ && (!filtroTipo || m.tipo_movimiento === filtroTipo) && (!filtroTec || m.tecnico_id === filtroTec);
  });

  const activos  = filtrados.filter(m => !m.deleted_at);
  const totales  = activos.reduce((a, m) => ({
    acreditado: a.acreditado + (m.monto_acreditado || 0),
    gastado:    a.gastado    + (m.total_movimiento  || 0),
  }), { acreditado: 0, gastado: 0 });
  const saldo = totales.acreditado - totales.gastado;

  const tituloResumen = esAdmin
    ? (filtroTec ? `Saldo de ${displayName(tecnicos.find(t => (t.id||t._id) === filtroTec))}` : 'Resumen global')
    : 'Mis movimientos';

  function openForm(mov = null) { setEditando(mov); setShowForm(true); }
  function closeForm()          { setShowForm(false); setEditando(null); }
  function openAcreditar(mov)   { setAcreditando(mov); }
  function closeAcreditar()     { setAcreditando(null); }
  function openSeleccionar()    { setSeleccionando(true); }
  function closeSeleccionar()   { setSeleccionando(false); }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* HEADER sticky */}
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-600 shadow-sm">
              <Car className="h-5 w-5 text-white" aria-hidden="true"/>
            </div>
            <div>
              <h1 className="text-base font-bold leading-none text-slate-900">Movilidad</h1>
              <p className="text-[11px] text-slate-500 mt-0.5">Pasajes y combustible</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => cargar(true)}
              aria-label="Actualizar lista"
              title="Actualizar"
              className={`${RING} rounded-xl border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-100`}
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true"/>
            </button>
            {esAdmin && (
              <button onClick={openSeleccionar}
                className={`${RING} hidden sm:inline-flex items-center gap-1.5 min-h-[40px] rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 active:bg-emerald-200 transition`}>
                <DollarSign className="h-4 w-4"/> Acreditar
              </button>
            )}
            <Btn onClick={() => openForm()} className="hidden sm:inline-flex min-h-[40px]">
              <Plus className="h-4 w-4"/> Nuevo movimiento
            </Btn>
            <Btn onClick={() => openForm()} className="sm:hidden min-h-[40px] min-w-[40px] p-0" aria-label="Nuevo movimiento">
              <Plus className="h-5 w-5"/>
            </Btn>
          </div>
        </div>
        {/* Tabs */}
        <div className="mx-auto flex max-w-7xl border-t border-slate-100 px-4 sm:px-6" role="tablist">
          {[
            { id: 'lista',     label: 'Movimientos', icon: Car     },
            { id: 'historial', label: 'Historial',   icon: History },
          ].map(tab => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={seccion === tab.id}
              onClick={() => setSeccion(tab.id)}
              className={`${RING} flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition-all duration-150
                ${seccion === tab.id
                  ? 'border-indigo-600 text-indigo-700'
                  : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'}`}
            >
              <tab.icon className="h-3.5 w-3.5" aria-hidden="true"/>
              {tab.label}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 space-y-6">

        {/* ══ LISTA ═══════════════════════════════════════════════════════════ */}
        {seccion === 'lista' && (
          <>
            {/* Métricas */}
            <section aria-label={tituloResumen}>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-slate-700">{tituloResumen}</h2>
                {esAdmin && !filtroTec && (
                  <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${SEM.amber}`}>
                    Filtrá un técnico para ver su saldo
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                <MetricCard icon={TrendingUp}   label="Acreditado" value={formatGs(totales.acreditado)} colorClass="bg-emerald-100 text-emerald-700"/>
                <MetricCard icon={TrendingDown} label="Gastado"    value={formatGs(totales.gastado)}    colorClass="bg-red-100 text-red-700"/>
                <MetricCard icon={Wallet}       label="Saldo"      value={formatGs(saldo)}              colorClass={saldo >= 0 ? 'bg-indigo-100 text-indigo-700' : 'bg-orange-100 text-orange-700'}/>
              </div>
            </section>

            {/* Filtros */}
            <section aria-label="Filtros" className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <Filter className="h-4 w-4 text-slate-400" aria-hidden="true"/>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Filtrar movimientos</h3>
                {(q || filtroTipo || filtroTec) && (
                  <button
                    onClick={() => { setQ(''); setFiltroTipo(''); setFiltroTec(''); }}
                    className="ml-auto text-xs font-medium text-indigo-600 hover:underline"
                    aria-label="Limpiar filtros"
                  >
                    Limpiar filtros
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true"/>
                  <input
                    type="search"
                    aria-label="Buscar movimientos"
                    placeholder="Buscá por descripción, técnico, origen…"
                    value={q}
                    onChange={e => setQ(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-4 text-sm placeholder:text-slate-400 transition hover:border-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="relative">
                  <select
                    aria-label="Filtrar por tipo"
                    value={filtroTipo}
                    onChange={e => setFiltroTipo(e.target.value)}
                    className="w-full appearance-none rounded-xl border border-slate-300 bg-white py-2.5 pl-3 pr-9 text-sm text-slate-700 transition hover:border-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:w-44"
                  >
                    <option value="">Todos los tipos</option>
                    {TIPOS.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"/>
                </div>
                {esAdmin && (
                  <div className="relative">
                    <select
                      aria-label="Filtrar por técnico"
                      value={filtroTec}
                      onChange={e => setFiltroTec(e.target.value)}
                      className="w-full appearance-none rounded-xl border border-slate-300 bg-white py-2.5 pl-3 pr-9 text-sm text-slate-700 transition hover:border-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:w-52"
                    >
                      <option value="">Todos los técnicos</option>
                      {tecnicos.map(t => <option key={t.id||t._id} value={t.id||t._id}>{displayName(t)}</option>)}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"/>
                  </div>
                )}
              </div>
              {filtrados.length > 0 && (
                <p className="mt-2.5 text-[11px] text-slate-500">
                  {filtrados.length} resultado{filtrados.length !== 1 ? 's' : ''}
                  {(q || filtroTipo || filtroTec) ? ' — filtro activo' : ''}
                </p>
              )}
            </section>

            {/* Lista de movimientos */}
            <section aria-label="Lista de movimientos">
              {loading ? (
                <>
                  <div className="sm:hidden space-y-3">{[1,2,3].map(i => <SkeletonCard key={i}/>)}</div>
                  <div className="hidden sm:block overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <table className="min-w-full divide-y divide-slate-100 text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          {['Fecha','Técnico','Tipo','Descripción','Monto',''].map((h,i) => (
                            <th key={i} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">{[1,2,3,4,5].map(i => <SkeletonRow key={i}/>)}</tbody>
                    </table>
                  </div>
                </>
              ) : filtrados.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 bg-white px-6 py-16 text-center">
                  <Car className="h-12 w-12 text-slate-300 mb-4" aria-hidden="true"/>
                  <p className="text-base font-semibold text-slate-700">
                    {q || filtroTipo || filtroTec ? 'Sin resultados para ese filtro' : 'Todavía no hay movimientos'}
                  </p>
                  <p className="mt-1 text-sm text-slate-500 max-w-xs">
                    {q || filtroTipo || filtroTec
                      ? 'Probá limpiar los filtros o cambiá los términos de búsqueda.'
                      : 'Registrá el primer pasaje o carga de combustible.'}
                  </p>
                  {(q || filtroTipo || filtroTec) && (
                    <Btn variant="secondary" className="mt-4" onClick={() => { setQ(''); setFiltroTipo(''); setFiltroTec(''); }}>
                      Limpiar filtros
                    </Btn>
                  )}
                </div>
              ) : (
                <>
                  {/* Mobile cards */}
                  <div className="sm:hidden space-y-2">
                    {filtrados.map(m => (
                      <article
                        key={m.id || m._id}
                        onClick={() => setDetalle(m)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => e.key === 'Enter' && setDetalle(m)}
                        aria-label={`Ver detalle de ${m.descripcion || 'movimiento'}`}
                        className={`cursor-pointer rounded-2xl border bg-white p-4 shadow-sm transition-all duration-150 active:scale-[0.99]
                          ${m.deleted_at ? 'border-red-200 bg-red-50/40' : 'border-slate-200 hover:border-slate-300 hover:shadow-md'}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-semibold text-slate-900 text-sm">{m.descripcion || '—'}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{tecnicoNombre(m, tecnicos)}</p>
                          </div>
                          <div className="shrink-0 text-right">
                            <p className={`font-bold text-sm ${m.tipo_movimiento === 'pasaje' ? 'text-sky-700' : 'text-amber-700'}`}>
                              {formatGs(m.total_movimiento ?? 0)}
                            </p>
                            <Pill tipo={m.tipo_movimiento} deleted={!!m.deleted_at}/>
                          </div>
                        </div>
                        <p className="mt-2 text-xs text-slate-400">{formatFecha(m.fecha || m.created_at)}</p>
                        {m.deleted_legend && (
                          <div className={`mt-2 rounded-xl px-3 py-2 text-[11px] font-medium ${SEM.danger}`}>{m.deleted_legend}</div>
                        )}
                        {!m.deleted_at && (
                          <div className="mt-3 flex items-center gap-2 pt-3 border-t border-slate-100" onClick={e => e.stopPropagation()}>
                            <button onClick={() => setDetalle(m)} aria-label="Ver detalle"
                              className={`${RING} flex min-h-[40px] items-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 active:bg-slate-100`}>
                              <Eye className="h-3.5 w-3.5"/> Ver
                            </button>
                            {esTecnico && (
                              <>
                                <button onClick={() => openForm(m)} aria-label="Editar"
                                  className={`${RING} flex min-h-[40px] items-center gap-1.5 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2 text-xs font-medium text-indigo-700 hover:bg-indigo-100 active:bg-indigo-200`}>
                                  <Edit className="h-3.5 w-3.5"/> Editar
                                </button>
                                <button onClick={() => eliminar(m.id || m._id)} aria-label="Eliminar"
                                  className={`${RING} flex min-h-[40px] items-center gap-1.5 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-xs font-medium text-red-600 hover:bg-red-100 active:bg-red-200`}>
                                  <Trash2 className="h-3.5 w-3.5"/>
                                </button>
                              </>
                            )}
                            {esAdmin && !m.deleted_at && (
                              <button onClick={e => { e.stopPropagation(); openAcreditar(m); }} aria-label="Acreditar"
                                className={`${RING} flex min-h-[40px] items-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-100 active:bg-emerald-200`}>
                                <DollarSign className="h-3.5 w-3.5"/> Acreditar
                              </button>
                            )}
                          </div>
                        )}
                      </article>
                    ))}
                  </div>

                  {/* Desktop tabla */}
                  <div className="hidden sm:block overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <table className="min-w-full divide-y divide-slate-100 text-sm" role="grid">
                      <thead className="bg-slate-50">
                        <tr>
                          {['Fecha','Técnico','Tipo','Descripción','Monto',...(esAdmin ? ['Acreditado'] : []),''].map((h,i) => (
                            <th key={i} scope="col"
                              className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 ${i >= (esAdmin ? 5 : 4) ? 'text-right' : 'text-left'}`}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {filtrados.map(m => (
                          <tr key={m.id || m._id}
                            className={`group transition-colors duration-100 ${m.deleted_at ? 'bg-red-50/50' : 'hover:bg-slate-50'}`}>
                            <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                              <div>{formatFecha(m.fecha || m.created_at)}</div>
                              {m.deleted_legend && (
                                <div className="mt-1 text-[10px] font-medium text-red-600 max-w-[160px] truncate" title={m.deleted_legend}>
                                  {m.deleted_legend}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 font-medium text-slate-800 max-w-[140px] truncate">{tecnicoNombre(m, tecnicos)}</td>
                            <td className="px-4 py-3"><Pill tipo={m.tipo_movimiento} deleted={!!m.deleted_at}/></td>
                            <td className="px-4 py-3 text-slate-600 max-w-xs truncate" title={m.descripcion}>{m.descripcion || '—'}</td>
                            <td className="px-4 py-3 text-right font-semibold tabular-nums text-slate-900">{formatGs(m.total_movimiento ?? 0)}</td>
                            {esAdmin && (
                              <td className="px-4 py-3 text-right tabular-nums text-sm text-slate-600">
                                {formatGs(m.monto_acreditado ?? 0)}
                              </td>
                            )}
                            <td className="px-4 py-3">
                              {/* Botones hover para tecnico; siempre visible para admin */}
                              <div className={`flex items-center justify-end gap-1 transition-opacity duration-150 ${esTecnico ? 'opacity-0 group-hover:opacity-100' : ''}`}>
                                <button onClick={() => setDetalle(m)} aria-label="Ver detalle"
                                  className={`${RING} rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700`}>
                                  <Eye className="h-4 w-4"/>
                                </button>
                                {esTecnico && !m.deleted_at && (
                                  <>
                                    <button onClick={() => openForm(m)} aria-label="Editar movimiento"
                                      className={`${RING} rounded-lg p-1.5 text-slate-400 hover:bg-indigo-50 hover:text-indigo-600`}>
                                      <Edit className="h-4 w-4"/>
                                    </button>
                                    <button onClick={() => eliminar(m.id || m._id)} aria-label="Eliminar movimiento"
                                      className={`${RING} rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600`}>
                                      <Trash2 className="h-4 w-4"/>
                                    </button>
                                  </>
                                )}
                                {esAdmin && !m.deleted_at && (
                                  <button onClick={() => openAcreditar(m)} aria-label="Acreditar movimiento"
                                    className={`${RING} inline-flex items-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 active:bg-emerald-200`}>
                                    <DollarSign className="h-3.5 w-3.5"/> Acreditar
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </section>
          </>
        )}

        {/* ══ HISTORIAL ═══════════════════════════════════════════════════════ */}
        {seccion === 'historial' && (
          <section aria-label="Historial verificable" className="space-y-5">
            <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-indigo-600">Auditoría</p>
                <h2 className="mt-0.5 text-xl font-bold text-slate-900">Historial de movilidad</h2>
                <p className="mt-1 text-sm text-slate-500">Verificá pasajes, combustibles y acreditaciones por período y técnico.</p>
              </div>
              <Btn variant="secondary" onClick={cargarHistorial} loading={histLoading}>
                <RefreshCw className="h-4 w-4"/> Actualizar
              </Btn>
            </header>

            {/* Filtros historial */}
            <div className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-3">
              {esAdmin && (
                <FSelect label="Técnico" value={histTecnico} onChange={setHistTecnico}>
                  <option value="all">Todos los técnicos</option>
                  {tecnicos.map(t => <option key={t.id||t._id} value={t.id||t._id}>{displayName(t)}</option>)}
                </FSelect>
              )}
              <FSelect label="Año" value={histAnio} onChange={v => setHistAnio(Number(v))}>
                {Array.from({ length: 5 }, (_, i) => currentYear - i).map(y => <option key={y} value={y}>{y}</option>)}
              </FSelect>
              <FSelect label="Mes" value={histMes} onChange={v => setHistMes(Number(v) || '')}>
                <option value="">Todo el año</option>
                {MESES.map((mes, i) => <option key={mes} value={i + 1}>{mes}</option>)}
              </FSelect>
            </div>

            {histLoading ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
                {[1,2,3,4,5,6].map(i => <SkeletonCard key={i}/>)}
              </div>
            ) : histError ? (
              <div className={`flex items-center gap-3 rounded-2xl p-4 ${SEM.danger}`}>
                <AlertCircle className="h-5 w-5 shrink-0"/>
                <p className="text-sm">{histError}</p>
              </div>
            ) : histData ? (
              <>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-3 xl:grid-cols-6">
                  <MetricCard icon={BarChart3}    label="Registros"  value={histData.resumen?.total_registros ?? 0}           colorClass="bg-slate-100 text-slate-700"/>
                  <MetricCard icon={CalendarDays} label="Pasajes"    value={histData.resumen?.cantidad_pasajes ?? 0}           colorClass="bg-sky-100 text-sky-700"/>
                  <MetricCard icon={TrendingUp}   label="Acreditado" value={formatGs(histData.resumen?.total_acreditado ?? 0)} colorClass="bg-emerald-100 text-emerald-700"/>
                  <MetricCard icon={TrendingDown} label="Gastado"    value={formatGs(histData.resumen?.total_gastado ?? 0)}   colorClass="bg-red-100 text-red-700"/>
                  <MetricCard icon={Wallet}       label="Saldo"      value={formatGs(histData.resumen?.saldo ?? 0)}           colorClass={Number(histData.resumen?.saldo||0) >= 0 ? 'bg-indigo-100 text-indigo-700' : 'bg-orange-100 text-orange-700'}/>
                  <MetricCard icon={Trash2}       label="Eliminados" value={histData.resumen?.eliminados ?? 0}                colorClass="bg-rose-100 text-rose-700"/>
                </div>

                {/* Tabla resumen mensual */}
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3">
                    <Users className="h-4 w-4 text-indigo-600" aria-hidden="true"/>
                    <h3 className="text-sm font-semibold text-slate-800">Resumen mensual</h3>
                  </div>
                  <div className="overflow-x-auto -webkit-overflow-scrolling-touch">
                    <table className="min-w-full divide-y divide-slate-100 text-sm">
                      <thead className="bg-white">
                        <tr>
                          {['Período','Registros','Pasajes','Acreditado','Gastado','Saldo','Eliminados'].map(h => (
                            <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {(histData.por_mes || []).length === 0 ? (
                          <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500">Sin movimientos para ese período.</td></tr>
                        ) : (histData.por_mes || []).map(item => {
                          const s = Number(item.resumen?.saldo || 0);
                          return (
                            <tr key={`${item.anio}-${item.mes}`} className="hover:bg-slate-50 transition-colors">
                              <td className="px-4 py-3 font-medium text-slate-900">{item.label}</td>
                              <td className="px-4 py-3 tabular-nums text-slate-600">{item.resumen?.total_registros ?? 0}</td>
                              <td className="px-4 py-3 tabular-nums text-slate-600">{item.resumen?.cantidad_pasajes ?? 0}</td>
                              <td className="px-4 py-3 tabular-nums text-slate-600">{formatGs(item.resumen?.total_acreditado ?? 0)}</td>
                              <td className="px-4 py-3 tabular-nums text-slate-600">{formatGs(item.resumen?.total_gastado ?? 0)}</td>
                              <td className={`px-4 py-3 tabular-nums font-semibold ${s >= 0 ? 'text-indigo-700' : 'text-orange-700'}`}>{formatGs(s)}</td>
                              <td className="px-4 py-3 tabular-nums text-slate-600">{item.resumen?.eliminados ?? 0}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Tabla detalle */}
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3">
                    <CalendarDays className="h-4 w-4 text-indigo-600" aria-hidden="true"/>
                    <h3 className="text-sm font-semibold text-slate-800">Movimientos incluidos</h3>
                    <span className="ml-auto rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                      {(histData.movimientos || []).length}
                    </span>
                  </div>
                  <div className="max-h-[480px] overflow-x-auto overflow-y-auto">
                    <table className="min-w-full divide-y divide-slate-100 text-sm">
                      <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_#e2e8f0]">
                        <tr>
                          {['Fecha','Técnico','Tipo','Descripción','Monto','Acreditado','Estado'].map(h => (
                            <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {(histData.movimientos || []).length === 0 ? (
                          <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500">Sin movimientos para mostrar.</td></tr>
                        ) : (histData.movimientos || []).map(m => (
                          <tr key={m.id || m._id} className={`transition-colors ${m.deleted_at ? 'bg-red-50/40' : 'hover:bg-slate-50'}`}>
                            <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{formatFecha(m.fecha || m.created_at)}</td>
                            <td className="px-4 py-3 text-slate-700 max-w-[120px] truncate">{m.nombre_tecnico || '—'}</td>
                            <td className="px-4 py-3"><Pill tipo={m.tipo_movimiento} deleted={!!m.deleted_at}/></td>
                            <td className="px-4 py-3 text-slate-600 max-w-[200px] truncate" title={m.descripcion}>{m.descripcion || '—'}</td>
                            <td className="px-4 py-3 tabular-nums text-slate-700">{formatGs(m.total_movimiento || 0)}</td>
                            <td className="px-4 py-3 tabular-nums text-slate-700">{formatGs(m.monto_acreditado || 0)}</td>
                            <td className="px-4 py-3">
                              {m.deleted_at ? (
                                <div>
                                  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${SEM.danger}`}>
                                    <X className="h-3 w-3"/> Eliminado
                                  </span>
                                  {m.deleted_legend && <p className="mt-1 text-[10px] text-red-600 max-w-[160px]">{m.deleted_legend}</p>}
                                </div>
                              ) : (
                                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${SEM.success}`}>
                                  <CheckCircle2 className="h-3 w-3"/> Activo
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 bg-white px-6 py-16 text-center">
                <History className="h-12 w-12 text-slate-300 mb-4"/>
                <p className="text-base font-semibold text-slate-700">Seleccioná los filtros para ver el historial</p>
                <p className="mt-1 text-sm text-slate-500">Elegí un técnico, año y mes para auditar los movimientos.</p>
              </div>
            )}
          </section>
        )}
      </main>

      {showForm && (
        <MovimientoModal
          movimiento={editando} tecnicos={tecnicos} user={user}
          onClose={closeForm}
          onSaved={() => { closeForm(); cargar(true); }}
        />
      )}
      {detalle && (
        <DetalleModal
          movimiento={detalle} tecnicos={tecnicos}
          onClose={() => setDetalle(null)}
          puedeEditar={esTecnico && !detalle.deleted_at}
          onEdit={() => { setDetalle(null); openForm(detalle); }}
          puedeAcreditar={esAdmin && !detalle.deleted_at}
          onAcreditar={() => { setDetalle(null); openAcreditar(detalle); }}
        />
      )}
      {seleccionando && (
        <SeleccionarMovimientoModal
          movimientos={movimientos.filter(m => !m.deleted_at)}
          tecnicos={tecnicos}
          onClose={closeSeleccionar}
          onSelect={mov => { closeSeleccionar(); openAcreditar(mov); }}
        />
      )}
      {acreditando && (
        <AcreditarModal
          movimiento={acreditando}
          onClose={closeAcreditar}
          onSaved={() => { closeAcreditar(); cargar(true); }}
        />
      )}
    </div>
  );
}

/* ─── MODAL NUEVO / EDITAR ──────────────────────────────────────────────────── */
function MovimientoModal({ movimiento, tecnicos, user, onClose, onSaved }) {
  const esAdmin   = user?.rol === 'admin';
  const esEdicion = !!movimiento;
  const uid       = user?.id || user?._id || '';
  const miNombre  = displayName(user);
  const fileRef   = useRef(null);

  const [saving,        setSaving]        = useState(false);
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [fotoFactura,   setFotoFactura]   = useState(movimiento?.foto_factura || null);

  const [form, setForm] = useState({
    tipo_movimiento:       movimiento?.tipo_movimiento       || 'pasaje',
    tecnico_id:            movimiento?.tecnico_id            || uid,
    nombre_tecnico:        movimiento?.nombre_tecnico        || miNombre,
    fecha:                 movimiento?.fecha ? String(movimiento.fecha).slice(0,10) : new Date().toISOString().slice(0,10),
    monto_acreditado:      movimiento?.monto_acreditado      || 0,
    cantidad_pasajes:      movimiento?.cantidad_pasajes      || '',
    costo_unitario_pasaje: movimiento?.costo_unitario_pasaje || '',
    descripcion:           movimiento?.descripcion           || '',
    observaciones:         movimiento?.observaciones         || '',
    origen:                movimiento?.origen                || '',
    destino:               movimiento?.destino               || '',
    lugar_carga:           movimiento?.lugar_carga           || '',
    tipo_combustible:      movimiento?.tipo_combustible      || 'nafta',
    numero_factura:        movimiento?.numero_factura        || '',
    nombre_proveedor:      movimiento?.nombre_proveedor      || '',
    litros:                movimiento?.litros                || '',
    kilometraje:           movimiento?.kilometraje           || '',
    monto_cargado:         movimiento?.monto_cargado         || '',
    orden_id:              movimiento?.orden_id              || '',
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const esCombustible = form.tipo_movimiento === 'combustible';

  const totalPreview = esCombustible
    ? (Number(form.monto_cargado) || 0)
    : (Number(form.cantidad_pasajes) || 0) * (Number(form.costo_unitario_pasaje) || 0);

  async function guardar() {
    if (!form.descripcion.trim())             return toast.error('Agregá una descripción para este movimiento.');
    if (!form.origen.trim() || !form.destino.trim()) return toast.error('Completá el origen y destino del recorrido.');
    if (!esCombustible) {
      if (!form.cantidad_pasajes)       return toast.error('Indicá cuántos pasajes se usaron.');
      if (!form.costo_unitario_pasaje)  return toast.error('Indicá el costo por pasaje.');
    }
    if (esCombustible && !form.monto_cargado) return toast.error('Indicá el monto total de la carga de combustible.');

    setSaving(true);
    try {
      const payload = {
        ...form,
        monto_acreditado:      Number(form.monto_acreditado || 0),
        cantidad_pasajes:      esCombustible ? null : Number(form.cantidad_pasajes),
        costo_unitario_pasaje: esCombustible ? null : Number(form.costo_unitario_pasaje),
        monto_cargado:         esCombustible ? Number(form.monto_cargado) : null,
        litros:                form.litros      ? Number(form.litros)      : null,
        kilometraje:           form.kilometraje ? Number(form.kilometraje) : null,
        foto_factura: fotoFactura,
      };
      if (!esAdmin) {
        payload.tecnico_id       = uid;
        payload.nombre_tecnico   = miNombre;
        payload.monto_acreditado = 0;
      }
      if (esEdicion) {
        await http.put(`/movilidad/${movimiento.id || movimiento._id}`, payload);
        toast.success('Movimiento actualizado correctamente.');
      } else {
        await http.post('/movilidad', payload);
        toast.success('Movimiento registrado correctamente.');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar. Revisá los datos e intentá de nuevo.');
    } finally { setSaving(false); }
  }

  async function subirFoto(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!esEdicion) return toast.error('Primero guardá el movimiento y luego subí la foto.');
    setUploadingFoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await http.post(`/movilidad/${movimiento.id || movimiento._id}/foto-factura`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFotoFactura(res.data);
      toast.success('Foto de factura subida correctamente.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo subir la foto.');
    } finally { setUploadingFoto(false); }
  }

  return (
    <Modal onClose={onClose} size="lg">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100">
          {esEdicion ? <Edit className="h-4 w-4 text-indigo-700"/> : <Plus className="h-4 w-4 text-indigo-700"/>}
        </div>
        <div>
          <h2 className="text-base font-bold text-slate-900">{esEdicion ? 'Editar movimiento' : 'Nuevo movimiento'}</h2>
          <p className="text-xs text-slate-500">{esEdicion ? 'Modificá los datos del movimiento' : 'Registrá un pasaje o carga de combustible'}</p>
        </div>
        <button onClick={onClose} aria-label="Cerrar" className={`${RING} ml-auto rounded-xl p-2 text-slate-400 hover:bg-slate-100`}>
          <X className="h-5 w-5"/>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">

        {/* Tipo — selector visual */}
        <div>
          <p className="mb-2 text-xs font-semibold text-slate-700">Tipo de movimiento <span className="text-red-500">*</span></p>
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Tipo de movimiento">
            {TIPOS.map(t => (
              <button key={t.value} type="button" role="radio" aria-checked={form.tipo_movimiento === t.value}
                onClick={() => set('tipo_movimiento', t.value)}
                className={`${RING} flex items-center gap-2 rounded-2xl border-2 px-4 py-3 text-sm font-semibold transition-all duration-150
                  ${form.tipo_movimiento === t.value
                    ? `border-indigo-500 ${t.bg} ${t.text} shadow-sm`
                    : 'border-slate-200 text-slate-600 hover:border-slate-300 bg-white'}`}>
                <span className="text-xl" aria-hidden="true">{t.icon}</span>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Técnico + Fecha */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {esAdmin ? (
            <FSelect label="Técnico" required value={form.tecnico_id}
              onChange={v => { const t = tecnicos.find(t => (t.id||t._id) === v); set('tecnico_id', v); set('nombre_tecnico', displayName(t)); }}>
              <option value="">Seleccioná un técnico…</option>
              {tecnicos.map(t => <option key={t.id||t._id} value={t.id||t._id}>{displayName(t)}</option>)}
            </FSelect>
          ) : (
            <FField label="Técnico" value={form.nombre_tecnico} readOnly hint="Tu nombre se asigna automáticamente."/>
          )}
          <FField label="Fecha" required type="date" value={form.fecha} onChange={v => set('fecha', v)}/>
        </div>

        {esCombustible && (
          <FField label="Monto total de carga (Gs.)" required type="number" value={form.monto_cargado}
            onChange={v => set('monto_cargado', v)} placeholder="Ej: 80 000"/>
        )}

        <FField label="Descripción" required value={form.descripcion} onChange={v => set('descripcion', v)}
          placeholder={esCombustible ? 'Ej: Carga nafta semanal' : 'Ej: Bus ida y vuelta a Lambaré'}/>

        <div className="grid grid-cols-2 gap-3">
          <FField label="Desde" required value={form.origen}  onChange={v => set('origen', v)}  placeholder="Ej: Asunción"/>
          <FField label="Hasta" required value={form.destino} onChange={v => set('destino', v)} placeholder="Ej: Lambaré"/>
        </div>

        {!esCombustible ? (
          <div className="grid grid-cols-2 gap-3">
            <FField label="Cantidad de pasajes" required type="number" value={form.cantidad_pasajes} onChange={v => set('cantidad_pasajes', v)} placeholder="Ej: 2"/>
            <FField label="Costo por pasaje (Gs.)" required type="number" value={form.costo_unitario_pasaje} onChange={v => set('costo_unitario_pasaje', v)} placeholder="Ej: 5 000"/>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FField label="N° de factura"   value={form.numero_factura}   onChange={v => set('numero_factura', v)}/>
            <FField label="Proveedor"       value={form.nombre_proveedor} onChange={v => set('nombre_proveedor', v)}/>
            <FField label="Lugar de carga"  value={form.lugar_carga}      onChange={v => set('lugar_carga', v)}/>
            <FField label="Kilometraje"     type="number" value={form.kilometraje} onChange={v => set('kilometraje', v)} placeholder="Ej: 45 000"/>
            <FSelect label="Tipo de combustible" value={form.tipo_combustible} onChange={v => set('tipo_combustible', v)}>
              <option value="nafta">Nafta</option>
              <option value="diesel">Diesel</option>
              <option value="gnv">GNV</option>
              <option value="electrico">Eléctrico</option>
              <option value="otro">Otro</option>
            </FSelect>
            <FField label="Litros cargados" type="number" value={form.litros} onChange={v => set('litros', v)} placeholder="Ej: 20"/>
          </div>
        )}

        {/* Preview total — feedback inmediato */}
        {totalPreview > 0 && (
          <div className="flex items-center justify-between rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3">
            <p className="text-sm font-medium text-indigo-700">Total estimado</p>
            <p className="text-lg font-bold tabular-nums text-indigo-900">{formatGs(totalPreview)}</p>
          </div>
        )}

        <FTextarea label="Observaciones" value={form.observaciones} onChange={v => set('observaciones', v)}
          placeholder="Detalles adicionales del recorrido o carga…" hint="Opcional."/>

        {/* Foto factura */}
        {esCombustible && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-slate-700">Foto de factura <span className="text-slate-400 font-normal">(opcional)</span></p>
              <div>
                <input ref={fileRef} type="file" accept="image/*" capture="environment" className="sr-only" onChange={subirFoto} aria-label="Subir foto de factura"/>
                <button type="button" onClick={() => fileRef.current?.click()}
                  disabled={uploadingFoto || !esEdicion}
                  className={`${RING} inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50`}>
                  {uploadingFoto ? <Loader2 className="h-4 w-4 animate-spin"/> : <Camera className="h-4 w-4"/>}
                  {uploadingFoto ? 'Subiendo…' : 'Adjuntar foto'}
                </button>
              </div>
            </div>
            {!esEdicion && (
              <div className={`flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-medium ${SEM.amber}`}>
                <AlertCircle className="h-4 w-4 shrink-0"/>
                Guardá el movimiento primero y luego subí la foto.
              </div>
            )}
            {fotoFactura?.url && (
              <img src={fotoFactura.url} alt="Foto de factura" className="w-full rounded-2xl border border-slate-200 bg-slate-50 object-contain max-h-40"/>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 border-t border-slate-100 bg-slate-50/80 px-5 py-4 rounded-b-2xl">
        <Btn variant="secondary" onClick={onClose} className="min-h-[44px]">Cancelar</Btn>
        <Btn onClick={guardar} loading={saving} className="flex-1 sm:flex-none sm:min-w-[160px] min-h-[44px]">  
          <Save className="h-4 w-4"/>
          {saving ? 'Guardando…' : esEdicion ? 'Guardar cambios' : 'Registrar movimiento'}
        </Btn>
      </div>
    </Modal>
  );
}

/* ─── MODAL DETALLE ──────────────────────────────────────────────────────────── */
function DetalleModal({ movimiento: m, tecnicos = [], onClose, puedeEditar, onEdit, puedeAcreditar, onAcreditar }) {
  return (
    <Modal onClose={onClose} size="md">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100">
          <Eye className="h-4 w-4 text-slate-600"/>
        </div>
        <div>
          <h2 className="text-base font-bold text-slate-900">Detalle del movimiento</h2>
          <p className="text-xs text-slate-500">{formatFecha(m.fecha || m.created_at)} · {tecnicoNombre(m, tecnicos)}</p>
        </div>
        <button onClick={onClose} aria-label="Cerrar" className={`${RING} ml-auto rounded-xl p-2 text-slate-400 hover:bg-slate-100`}>
          <X className="h-5 w-5"/>
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-5">
        {m.deleted_legend && (
          <div className={`mb-4 flex items-start gap-2 rounded-2xl px-4 py-3 ${SEM.danger}`}>
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0"/>
            <p className="text-sm">{m.deleted_legend}</p>
          </div>
        )}
        <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
          <DetailRow label="Estado"         value={<Pill tipo={m.tipo_movimiento} deleted={!!m.deleted_at}/>}/>
          <DetailRow label="Fecha"          value={formatFecha(m.fecha || m.created_at)}/>
          <DetailRow label="Técnico"        value={tecnicoNombre(m, tecnicos)}/>
          <DetailRow label="Descripción"    value={m.descripcion}/>
          <DetailRow label="Desde"          value={m.origen}/>
          <DetailRow label="Hasta"          value={m.destino}/>
          <DetailRow label="Monto total"    value={<span className="font-bold">{formatGs(m.total_movimiento || 0)}</span>}/>
          <DetailRow label="Acreditado"     value={formatGs(m.monto_acreditado || 0)}/>
          <DetailRow label="Pasajes"        value={m.cantidad_pasajes}/>
          <DetailRow label="Costo/pasaje"   value={m.costo_unitario_pasaje ? formatGs(m.costo_unitario_pasaje) : null}/>
          <DetailRow label="Monto cargado"  value={m.monto_cargado ? formatGs(m.monto_cargado) : null}/>
          <DetailRow label="N° factura"     value={m.numero_factura}/>
          <DetailRow label="Proveedor"      value={m.nombre_proveedor}/>
          <DetailRow label="Lugar de carga" value={m.lugar_carga}/>
          <DetailRow label="Combustible"    value={m.tipo_combustible}/>
          <DetailRow label="Litros"         value={m.litros}/>
          <DetailRow label="Kilometraje"    value={m.kilometraje}/>
          <DetailRow label="Observaciones"  value={m.observaciones}/>
        </div>
        {m.foto_factura?.url && (
          <div className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Foto de factura</p>
            <img src={m.foto_factura.url} alt="Foto de factura adjunta" className="w-full rounded-2xl border border-slate-200 bg-slate-50 object-contain max-h-52"/>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 bg-slate-50/80 px-5 py-4 rounded-b-2xl">
        <Btn variant="secondary" onClick={onClose} className="flex-1 sm:flex-none min-h-[44px]">Cerrar</Btn>
        {puedeAcreditar && onAcreditar && (
          <Btn variant="secondary" onClick={onAcreditar} className="flex-1 sm:flex-none min-h-[44px] border-emerald-300 text-emerald-700 hover:bg-emerald-50">
            <DollarSign className="h-4 w-4"/> Acreditar
          </Btn>
        )}
        {puedeEditar && onEdit && (
          <Btn onClick={onEdit} className="flex-1 sm:flex-none min-h-[44px]">
            <Edit className="h-4 w-4"/> Editar
          </Btn>
        )}
      </div>
    </Modal>
  );
}

/* ─── MODAL SELECCIONAR MOVIMIENTO PARA ACREDITAR ──────────────────────────── */
function SeleccionarMovimientoModal({ movimientos, tecnicos, onClose, onSelect }) {
  const [q, setQ] = useState('');
  const filtrados = movimientos.filter(m => {
    if (!q) return true;
    const txt = q.toLowerCase();
    return (
      (m.descripcion || '').toLowerCase().includes(txt) ||
      tecnicoNombre(m, tecnicos).toLowerCase().includes(txt) ||
      (m.tipo_movimiento || '').toLowerCase().includes(txt)
    );
  });

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}/>
      <div className="relative z-10 w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[85dvh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-slate-100">
          <div>
            <h3 className="text-base font-bold text-slate-900">Seleccionar movimiento</h3>
            <p className="text-xs text-slate-500 mt-0.5">Elegí el movimiento a acreditar</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5"/>
          </button>
        </div>
        {/* Buscador */}
        <div className="px-4 py-3 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" aria-hidden/>
            <input
              autoFocus
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Buscá por técnico, descripción, tipo…"
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            />
          </div>
        </div>
        {/* Lista */}
        <div className="overflow-y-auto flex-1 divide-y divide-slate-100">
          {filtrados.length === 0 && (
            <p className="py-10 text-center text-sm text-slate-400">Sin resultados</p>
          )}
          {filtrados.map(m => {
            const tipo = TIPOS.find(t => t.value === m.tipo_movimiento) || TIPOS[0];
            return (
              <button key={m.id || m._id} onClick={() => onSelect(m)}
                className="w-full text-left px-5 py-3.5 hover:bg-slate-50 active:bg-slate-100 transition flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${tipo.bg} ${tipo.text}`}>
                      {tipo.label}
                    </span>
                    <span className="text-xs font-medium text-slate-700 truncate">{tecnicoNombre(m, tecnicos)}</span>
                  </div>
                  <p className="text-xs text-slate-500 truncate mt-0.5">{m.descripcion || '—'}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{formatFecha(m.fecha || m.created_at)}</p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-semibold tabular-nums text-slate-900">{formatGs(m.total_movimiento ?? 0)}</p>
                  <p className="text-[10px] text-slate-400">Acreditado: {formatGs(m.monto_acreditado ?? 0)}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ─── MODAL ACREDITAR ────────────────────────────────────────────────────────── */
function AcreditarModal({ movimiento: m, onClose, onSaved }) {
  const [monto,   setMonto]   = useState(String(m?.monto_acreditado ?? 0));
  const [saving,  setSaving]  = useState(false);

  const tecnico   = titleCase(m?.nombre_tecnico || '');
  const tipo      = TIPOS.find(t => t.value === m?.tipo_movimiento) || TIPOS[0];
  const gastado   = m?.total_movimiento ?? 0;
  const saldoPrev = (Number(m?.monto_acreditado) || 0) - gastado;
  const saldoNew  = (Number(monto) || 0) - gastado;

  async function guardar() {
    const valor = parseFloat(monto);
    if (isNaN(valor) || valor < 0) return toast.error('Ingresá un monto válido, mayor o igual a 0.');
    setSaving(true);
    try {
      await http.patch(`/movilidad/${m.id || m._id}/acreditar`, { monto_acreditado: valor });
      toast.success(`Acreditación de ${formatGs(valor)} registrada correctamente.`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar la acreditación.');
    } finally { setSaving(false); }
  }

  return (
    <Modal onClose={onClose} size="sm">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100">
          <DollarSign className="h-4 w-4 text-emerald-700"/>
        </div>
        <div>
          <h2 className="text-base font-bold text-slate-900">Registrar acreditación</h2>
          <p className="text-xs text-slate-500">{tecnico} · <span className={`${tipo.text}`}>{tipo.icon} {tipo.label}</span></p>
        </div>
        <button onClick={onClose} aria-label="Cerrar" className={`${RING} ml-auto rounded-xl p-2 text-slate-400 hover:bg-slate-100`}>
          <X className="h-5 w-5"/>
        </button>
      </div>

      {/* Body */}
      <div className="px-5 py-5 space-y-4">
        {/* Resumen del movimiento */}
        <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 space-y-1.5 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-500">Descripción</span>
            <span className="font-medium text-slate-800 text-right max-w-[60%] truncate">{m?.descripcion || '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Fecha</span>
            <span className="text-slate-800">{formatFecha(m?.fecha || m?.created_at)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Gasto total</span>
            <span className="font-semibold tabular-nums text-red-700">{formatGs(gastado)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Acreditado actual</span>
            <span className="tabular-nums text-slate-700">{formatGs(m?.monto_acreditado ?? 0)}</span>
          </div>
          <div className="flex justify-between border-t border-slate-200 pt-1.5">
            <span className="text-slate-500 font-medium">Saldo actual</span>
            <span className={`font-bold tabular-nums ${saldoPrev >= 0 ? 'text-indigo-700' : 'text-orange-700'}`}>{formatGs(saldoPrev)}</span>
          </div>
        </div>

        {/* Input */}
        <FField
          label="Nuevo monto acreditado (Gs.)"
          required
          type="number"
          value={monto}
          onChange={setMonto}
          placeholder="Ej: 50 000"
          hint="Importe que la empresa entrega al técnico como adelanto o reembolso."
        />

        {/* Preview saldo nuevo */}
        {monto !== '' && !isNaN(Number(monto)) && (
          <div className={`flex items-center justify-between rounded-2xl px-4 py-3 border ${saldoNew >= 0 ? 'border-indigo-200 bg-indigo-50' : 'border-orange-200 bg-orange-50'}`}>
            <p className={`text-sm font-medium ${saldoNew >= 0 ? 'text-indigo-700' : 'text-orange-700'}`}>Saldo resultante</p>
            <p className={`text-lg font-bold tabular-nums ${saldoNew >= 0 ? 'text-indigo-900' : 'text-orange-900'}`}>{formatGs(saldoNew)}</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center gap-3 border-t border-slate-100 bg-slate-50/80 px-5 py-4 rounded-b-2xl">
        <Btn variant="secondary" onClick={onClose} className="min-h-[44px]">Cancelar</Btn>
        <Btn onClick={guardar} loading={saving} className="flex-1 min-h-[44px]">
          <Save className="h-4 w-4"/>
          {saving ? 'Guardando…' : 'Guardar acreditación'}
        </Btn>
      </div>
    </Modal>
  );
}
